import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";

/** The Department's requests to parties outside its file: market-study data from undertakings, technical opinions from regulators (G2G). */
export type RequestKind = "MarketData" | "TechnicalOpinion";
export type RequestStatus = "Sent" | "Opened" | "Submitted" | "ClarificationRequested" | "Completed" | "Withdrawn" | "Lapsed";
export type ItemKind = "Text" | "Number" | "Sales" | "Document";
export type OpinionPosition = "NoConcerns" | "ConcernsAddressable" | "SeriousConcerns" | "OutsideRemit";

export interface RequestItem { key: string; kind: ItemKind; label: string; labelAr?: string | null; hint?: string | null; hintAr?: string | null; unit?: string | null; required: boolean; years: number[] }
export interface SalesRow { productLine: string; hsCode?: string | null; year: number; flow: "Domestic" | "Import" | "Export"; valueAed?: number | null; volume?: number | null; volumeUnit?: string | null }
export interface RequestAnswer { key: string; text?: string | null; number?: number | null; rows: SalesRow[]; confidential: boolean }
export interface RequestAttachment { id: string; side: "Ministry" | "Recipient"; itemKey?: string | null; fileName: string; contentType: string; sizeBytes: number; confidential: boolean; uploadedAt: string; uploadedByName: string; submitted: boolean }
export interface RequestEvent { at: string; kind: string; actorName: string; side: string; note?: string | null }
/** One round of the answer as it was submitted; an answer resubmitted after a clarification never replaces the earlier round (S 2.7). */
export interface RequestRound {
  round: number; submittedAt: string; submittedByName?: string | null; submittedByTitle?: string | null; answers: RequestAnswer[]; position?: OpinionPosition | null;
  statement?: string | null; marketAssessment?: string | null; sources?: string | null; proposedConditions?: string | null; confidentialityNote?: string | null;
}

export interface RequestRow {
  id: string; kind: RequestKind; reference: string; status: RequestStatus; overdue: boolean; daysLeft: number; recipientName: string; recipientNameAr: string;
  recipientSector?: string | null; recipientSectorAr?: string | null; subject: string; subjectAr?: string | null; sentAt: string; dueOn: string; submittedAt?: string | null;
  caseId?: string | null; caseNumber?: string | null; studyId?: string | null; round: number; extensionRequestedTo?: string | null; position?: OpinionPosition | null;
  coordinationEntryId?: string | null;
}

export interface MinistryRequest extends Omit<RequestRow, "position"> {
  studyTitle?: string | null; studyTitleAr?: string | null; marketId?: string | null;
  recipientOrganizationId?: string | null; recipientEntityId?: string | null; contactName?: string | null; contactEmail?: string | null;
  summary: string; summaryAr?: string | null; context?: string | null; contextAr?: string | null; legalBasis: string; legalBasisAr: string;
  items: RequestItem[]; openedAt?: string | null; closedAt?: string | null; issuedByName: string;
  answers: RequestAnswer[]; position?: OpinionPosition | null; statement?: string | null; marketAssessment?: string | null; sources?: string | null; proposedConditions?: string | null;
  confidentialityNote?: string | null; declared: boolean; submittedByName?: string | null; submittedByTitle?: string | null; attachments: RequestAttachment[];
  reviewNote?: string | null; reviewedByName?: string | null; extensionReason?: string | null; timeline: RequestEvent[]; salesRowsFiled: number;
  /** On a test file the request reaches no one outside the Department but the account that filed the file (condition 1.10). */
  testFile?: boolean;
  /** Sales lines the Data Bank's other sources do not bear out (condition 1.9): accepted only with the officer's reasons. */
  crossCheck?: SalesCrossCheckFinding[] | null;
  /** The answer as submitted in each round before the one shown (S 2.7). */
  earlierRounds?: RequestRound[] | null;
}

export interface SalesCrossCheckFinding { kind: "line" | "revenue" | "market"; productLine: string; year: number; flow: string; answered: number; other: number; source?: string | null; sourceReference?: string | null; message: string; messageAr: string }

/**
 * The market-study file a study runs under, whether it is a test file whose requests reach no one outside the Department, the stage of the file's
 * procedure the study stands at, and whether the signed-in officer may open or tie the file (the act of opening a market study).
 */
export interface StudyFile { studyId: string; caseId?: string | null; caseNumber?: string | null; isTest: boolean; state?: string | null; stateLabel?: string | null; stateLabelAr?: string | null; mayTie?: boolean }

export interface OpinionBodyOption { key: string; nameEn: string; nameAr: string; sectorEn: string; sectorAr: string; role: "SectoralRegulator" | "RelevantAuthority"; suggested: boolean; focalPointEmail?: string | null; organizationId?: string | null }
export interface OpinionDraft {
  caseId: string; caseNumber: string; caseType: string; subject: string; subjectAr: string; summary: string; summaryAr: string; context: string; contextAr: string;
  questions: RequestItem[]; dueOn: string; bodies: OpinionBodyOption[]; coordination: Array<{ id: string; authority: string; authorityAr?: string | null; status: string; bodyKey?: string | null }>;
}

export const OPEN_STATUSES: RequestStatus[] = ["Sent", "Opened", "ClarificationRequested"];

export const requestKeys = {
  all: ["requests"] as const,
  list: (filter: { studyId?: string | null; caseId?: string | null; kind?: RequestKind | null; open?: boolean }) => ["requests", "list", filter] as const,
  one: (id: string | null) => ["requests", "one", id] as const,
};

export function useRequests(filter: { studyId?: string | null; caseId?: string | null; kind?: RequestKind | null; open?: boolean }, enabled = true) {
  const api = useApi();
  const params: Record<string, string> = {};
  if (filter.studyId) params.studyId = filter.studyId;
  if (filter.caseId) params.caseId = filter.caseId;
  if (filter.kind) params.kind = filter.kind;
  if (filter.open) params.open = "true";
  return useQuery({ queryKey: requestKeys.list(filter), queryFn: () => api.get<RequestRow[]>("/api/requests", params), enabled });
}

export function useRequest(id: string | null) {
  const api = useApi();
  return useQuery({ queryKey: requestKeys.one(id), queryFn: () => api.get<MinistryRequest>(`/api/requests/${id}`), enabled: !!id });
}

export function useInvalidateRequests() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: requestKeys.all });
    // An opinion request moves the concentration's coordination record and, with it, the statutory clock.
    void qc.invalidateQueries({ queryKey: ["ec-coordination"] });
    void qc.invalidateQueries({ queryKey: ["case"] });
  };
}

/** Badge tone: waiting on the addressee amber, an answer to read blue, overdue red, closed green or grey. */
export function statusTone(status: RequestStatus, overdue: boolean): "warning" | "danger" | "info" | "success" | "default" | "outline" {
  if (overdue && OPEN_STATUSES.includes(status)) return "danger";
  switch (status) {
    case "Sent": case "Opened": case "ClarificationRequested": return "warning";
    case "Submitted": return "info";
    case "Completed": return "success";
    default: return "outline";
  }
}
